import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import MoreBtn from 'ati-ui-react/components/MoreBtn';
import getNameTransportType from 'utils/getNameTransportType';
import cn from 'classnames';
import getTransportCategory from 'utils/getTransportCategory';
import { SidebarContainer, SidebarSection, HR, MoreBtnWrapper } from './SidebarBlockStyles';

import TruckName from '../TruckName/TruckName';
import Year from '../Year';
import Mileage from '../Mileage';
import Volume from '../Volume';
import Capacity from '../Capacity';
import Dimensions from '../Dimensions';
import BodyType from '../BodyType';
import CabinType from '../CabinType';
import SuspensionType from '../SuspensionType';
import DriveType from '../DriveType';
import Speed from '../Speed';
import FuelConsumption from '../FuelConsumption';
import Tank from '../Tank';
import ExtraOptions from '../ExtraOptions';
import TractorType from '../TractorType';
import SaddleHeight from '../SaddleHeight';
import Engine from '../Engine';
import Gearbox from '../Gearbox';
import BusType from '../BusType';
import SeatsCount from '../SeatsCount';
import SpecialMachineType from '../SpecialMachineType';
import UpperHeight from '../UpperHeight';
import Departure from '../Departure';
import BucketVolume from '../BucketVolume';
import MaxDiggingHeight from '../MaxDiggingHeight';
import MaxDiggingDepth from '../MaxDiggingDepth';
import DumpingHeight from '../DumpingHeight';
import BladeWidth from '../BladeWidth';
import TracksWidth from '../TracksWidth';
import FullyWeight from '../FullyWeight';
import WorkingHours from '../WorkingHours';
import GroundPressure from '../GroundPressure';
import MaxCuttingDepth from '../MaxCuttingDepth';
import MaxLooseningDepth from '../MaxLooseningDepth';
import BladePositionHeight from '../BladePositionHeight';
import SpeedWithoutLoad from '../SpeedWithoutLoad';
import SpeedWithLoad from '../SpeedWithLoad';
import LoadUppingSpeed from '../LoadUppingSpeed';
import OwnerCount from '../OwnerCount';
import WorkingWeight from '../WorkingWeight';
import Vin from '../Vin';
import GovNum from '../GovNum';

const SidebarBlock = ({ advert, item, type }) => {
  const truckNameBlockRef = useRef(null);
  const commonBlockRef = useRef(null);
  const additionalBlockRef = useRef(null);
  const specialMachineBlockRef = useRef(null);
  const vinBlockRef = useRef(null);

  const getCountSectionRows = () => {
    return {
      common: commonBlockRef.current?.childElementCount || 0,
      additional: additionalBlockRef.current?.childElementCount || 0,
      special: specialMachineBlockRef.current?.childElementCount || 0,
      vin: vinBlockRef.current?.childElementCount || 0,
    };
  };

  const [countSectionRows, setCountSectionRows] = useState(getCountSectionRows());
  const [isHideParametersUnderButton, setHideParametersUnderButton] = useState(false);

  useEffect(() => {
    const rows = getCountSectionRows();
    setCountSectionRows(rows);
    const needHideParameters = rows.additional + rows.special > 3;
    setHideParametersUnderButton(needHideParameters);
  }, []);

  const isPurchase = advert.advert_type === 'purchase';
  const isSpecialMachine = getTransportCategory(type) === 'special_machinery';
  const isRoadTrain = !!advert.advert_items;
  const isVinGovNum = item && (!isEmpty(item.vin) || !isEmpty(item.gov_num));

  const MoreButton = () => (
    <MoreBtnWrapper>
      <MoreBtn className="more-btn" action={() => setHideParametersUnderButton(false)}>
        Все характеристики
      </MoreBtn>
    </MoreBtnWrapper>
  );

  const countsList = [
    countSectionRows.common,
    countSectionRows.special,
    countSectionRows.additional,
    countSectionRows.vin,
  ];

  const hasTruckName = isRoadTrain && !!(item.brand?.name || item.model?.model);
  const isEmptyParameters = countsList.every((el) => !el);
  const isHideBlock = isEmptyParameters && !hasTruckName;

  // Section's classNames
  const commonSectionClassName = cn(!countSectionRows.common && 'hide');
  const specialSectionClassName = cn(
    'can-be-hidden',
    !countSectionRows.special && 'hide',
    isHideParametersUnderButton && 'hide-under-button',
  );
  const additionalSectionClassName = cn(
    'can-be-hidden',
    !countSectionRows.additional && 'hide',
    isHideParametersUnderButton && 'hide-under-button',
  );
  const vinSectionClassName = cn(!countSectionRows.vin && 'hide');

  // Hr's classNames
  const truckNameHrClassName = cn(countsList.every((el) => !el) && 'hide');
  const commonHrClassName = cn(
    countsList.slice(1).every((el) => !el) && 'hide',
    isHideParametersUnderButton && !countSectionRows.vin && 'hide-under-button',
  );
  const specialHrClassName = cn(countsList.slice(2).every((el) => !el) && 'hide');
  const additionalHrClassName = cn(countsList.slice(3).every((el) => !el) && 'hide');

  return (
    <SidebarContainer className={cn(isHideBlock && 'hide')}>
      {isRoadTrain && (
        <SidebarSection>
          <table>
            <tbody>
              <TruckName
                type={getNameTransportType(type)}
                truckName={item.brand?.name}
                truckModel={item.model?.model}
              />
            </tbody>
          </table>
          <HR className={truckNameHrClassName} />
        </SidebarSection>
      )}

      <SidebarSection className={commonSectionClassName}>
        <table>
          <tbody ref={commonBlockRef}>
            <Year year={item.year} isPurchase={isPurchase} before={isPurchase && 'от '} />
            <TractorType tractorType={item.tractor_type} />
            <BusType busType={item.bus_type} />
            <Mileage mileage={item.mileage} isPurchase={isPurchase} />
            <WorkingHours workingHours={item.working_hours} isPurchase={isPurchase} />
            <OwnerCount count={item.owner_count} />
            <Engine
              engineType={item.engine_type}
              enginePower={item.engine_power}
              engineVolume={item.engine_volume}
            />
            <Gearbox gearboxType={item.gearbox_type} />
            <Volume volume={item.volume} isPurchase={isPurchase} />
            <Capacity capacity={item.capacity} isPurchase={isPurchase} />
            <Dimensions width={item.width} height={item.height} length={item.length} />
            <BodyType bodyType={item.body_type} brand={item.body_brand} />
            <CabinType cabinType={item.cabin_type} />
            <WorkingWeight workingWeight={item.working_weight} />
          </tbody>
        </table>
        {isHideParametersUnderButton && <MoreButton />}
        <HR className={commonHrClassName} />
      </SidebarSection>

      {isSpecialMachine && (
        <SidebarSection className={specialSectionClassName}>
          <table>
            <tbody ref={specialMachineBlockRef}>
              <SpecialMachineType
                advertItemType={getNameTransportType(type, {
                  special_type: item.special_type,
                })}
                excavatorType={item.excavator_type}
                tractionClass={item.traction_class}
                loaderType={item.loader_type}
              />
              <UpperHeight maxJibUpperHeight={item.max_jib_upper_height} />
              <Departure
                maxJibDeparture={item.max_jib_departure}
                minJibDeparture={item.min_jib_departure}
              />
              <BucketVolume excavatorBucketVolume={item.excavator_bucket_volume} />
              <MaxDiggingHeight maxDiggingHeight={item.max_digging_height} />
              <MaxDiggingDepth maxDiggingDepth={item.max_digging_depth} />
              <DumpingHeight dumpingHeight={item.dumping_height} />
              <BladeWidth bladeWidth={item.blade_width} />
              <TracksWidth tracksWidth={item.tracks_width} />
              <FullyWeight fullyWeight={item.fully_weight} />
              <GroundPressure groundPressure={item.ground_pressure} />
              <MaxCuttingDepth maxCuttingDepth={item.max_cutting_depth} />
              <MaxLooseningDepth maxLooseningDepth={item.max_loosening_depth} />
              <BladePositionHeight maxBladePositionHeight={item.max_blade_position_height} />
              <SpeedWithoutLoad speedWithoutLoad={item.speed_without_load} />
              <SpeedWithLoad speedWithLoad={item.speed_with_load} />
              <LoadUppingSpeed loadUppingSpeed={item.load_upping_speed} />
            </tbody>
          </table>
          <HR className={specialHrClassName} />
        </SidebarSection>
      )}

      <SidebarSection className={additionalSectionClassName}>
        <table>
          <tbody ref={additionalBlockRef}>
            <SeatsCount seatsCount={item.seats_count} isPurchase={isPurchase} />
            <SaddleHeight saddleHeight={item.saddle_height} />
            <SuspensionType suspensionType={item.suspension_type} />
            <DriveType wheelFormula={item.wheel_formula} driveType={item.drive_type} />
            <Speed maxSpeed={item.max_speed} />
            <FuelConsumption fuelConsumption={item.fuel_consumption_100} />
            <Tank tankVolume={item.tank_volume} tanksCount={item.tanks_count} />
            <ExtraOptions
              withDriver={advert.with_driver}
              rightSideHelm={item.right_side_helm}
              computer={item.computer}
              centralOilManagement={item.central_oil_management}
              conditioner={item.conditioner}
              heaterInCabin={item.heater_in_cabin}
              enginePreheater={item.engine_preheater}
            />
          </tbody>
        </table>
        <HR className={additionalHrClassName} />
      </SidebarSection>

      {isVinGovNum && (
        <SidebarSection className={vinSectionClassName}>
          <table>
            <tbody ref={vinBlockRef}>
              <Vin vin={item.vin} />
              <GovNum govNum={item.gov_num} />
            </tbody>
          </table>
        </SidebarSection>
      )}
    </SidebarContainer>
  );
};

export default SidebarBlock;
